// src/modules/IntegratedMessaging/components/modals/CreateIntegrationModal.tsx

import { forwardRef, useImperativeHandle, useRef, useState, useEffect, ForwardedRef, FormEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { PasswordInput } from '@shared/components/ui/PasswordInput';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@shared/components/ui/select';
import { Checkbox } from '@shared/components/ui/checkbox';
import { useToast } from '@shared/hooks/use-toast';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useCreateIntegration } from '../../hooks/useCreateIntegration';
import { useCountries } from '../../hooks/useCountries';
import { useTeams } from '../../hooks/useTeams';
import { MultiSelect } from '@shared/components/ui/MultiSelect';
import type { Courier } from '../../models/IntegratedMessaging';

export interface CreateIntegrationModalRef {
  open: (courier: Courier) => void;
}

export const CreateIntegrationModal = forwardRef((_: {}, ref: ForwardedRef<CreateIntegrationModalRef>) => {
  const { t } = useTranslation('integratedmessagingModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const { toast } = useToast();
  const createMut = useCreateIntegration();
  const { data: countries = [] } = useCountries();
  const { data: teams = [] } = useTeams();
  const { getTeamType } = usePermissions();

  const [courier, setCourier] = useState<Courier | null>(null);
  const [form, setForm] = useState<Record<string, any>>({});
  const [visibility, setVisibility] = useState<'public' | 'private'>('public');
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);

  // Verificar si el usuario es 3pl
  const is3pl = getTeamType() === 'tenant_3pl';

  useImperativeHandle(ref, () => ({
    open: (c) => setCourier(c),
  }));

  // Opciones para el MultiSelect de teams - debe estar antes del early return
  const teamOptions = useMemo(
    () =>
      teams.map((team) => ({
        value: team.id.toString(),
        label: team.name,
      })),
    [teams],
  );

  useEffect(() => {
    if (!courier) return;
    modalRef.current?.openModal();
    // La API puede devolver column_data o columns_data
    const cols: string[] = (courier as any).column_data ?? courier.columns_data ?? [];
    setForm(
      cols.reduce(
        (acc, key) => {
          acc[key] = '';
          return acc;
        },
        { name: '' } as Record<string, any>,
      ),
    );
    // Resetear campos 3pl
    setVisibility('public');
    setSelectedTeamIds([]);
  }, [courier]);

  if (!courier) return null;

  const fields: string[] = (courier as any).column_data ?? courier.columns_data ?? [];
  const isPrivate = visibility === 'private';
  const handleChange = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!courier) return;
    createMut.mutate(
      {
        courier_id: courier.id,
        name: form.name,
        data: fields.reduce<Record<string, any>>((acc, key) => {
          acc[key] = form[key];
          return acc;
        }, {}),
        // Solo incluir campos 3pl si el usuario es 3pl
        ...(is3pl && {
          visibility,
          private_team_ids: selectedTeamIds.length > 0 ? selectedTeamIds.map((id) => parseInt(id, 10)) : undefined,
        }),
      },
      {
        onSuccess: () => {
          toast({ title: t('messages.created') });
          modalRef.current?.closeModal();
        },
        onError: (err: any) =>
          toast({
            title: t('messages.create_error'),
            description: err?.message || 'Error desconocido',
            variant: 'destructive',
          }),
      },
    );
  };

  return (
    <ModalCenter
      ref={modalRef}
      onCloseModal={() => setCourier(null)}
      title={t('modal.create.title', { name: courier.name })}
      type="default"
      width="lg"
      className="h-[calc(100%-30px)] sm:h-auto max-h-[90vh] overflow-y-auto"
    >
      <form onSubmit={handleSubmit} autoComplete="off" className="space-y-4 flex flex-col justify-between">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Nombre personalizado */}
          <div className="sm:col-span-2">
            <label className="text-sm font-medium text-text mb-1 block">{t('modal.fields.name_label')}</label>
            <Input
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder={t('modal.fields.name_placeholder')}
            />
          </div>

          {fields.map((key, idx) => {
            const lc = key.toLowerCase();
            const label = t(`modal.fields.${lc}_label`, {
              defaultValue: key,
            });
            const placeholder = t(`modal.fields.${lc}_placeholder`, {
              defaultValue: '',
            });

            if (lc === 'sender_country_code') {
              const isLastOdd = idx === fields.length - 1 && fields.length % 2 === 1;
              return (
                <div key={key} className={isLastOdd ? 'sm:col-span-2' : ''}>
                  <label className="text-sm font-medium text-text mb-1 block">{label}</label>
                  <Select value={form[key]} onValueChange={(v) => handleChange(key, v)}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={placeholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((c) => (
                        <SelectItem key={c.iso2} value={c.iso2}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            }

            const Field = lc.includes('password') ? PasswordInput : Input;
            const isLastOdd = idx === fields.length - 1 && fields.length % 2 === 1;

            return (
              <div key={key} className={isLastOdd ? 'sm:col-span-2' : ''}>
                <label className="text-sm font-medium text-text mb-1 block">{label}</label>
                <Field
                  value={form[key] ?? ''}
                  onChange={(e) => handleChange(key, e.target.value)}
                  placeholder={placeholder}
                  autoComplete={lc.includes('password') ? 'new-password' : undefined}
                />
              </div>
            );
          })}

          {/* Campos adicionales para usuarios 3pl */}
          {is3pl && (
            <>
              {/* Checkbox de visibilidad privada */}
              <div className="sm:col-span-1">
                <label className="text-sm font-medium text-text mb-1 block">
                  {t('modal.fields.visibility_label', {
                    defaultValue: 'Visibilidad',
                  })}
                </label>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="visibility-private"
                    checked={isPrivate}
                    onCheckedChange={(checked) => setVisibility(checked ? 'private' : 'public')}
                    data-testid="visibility-checkbox"
                  />
                  <label
                    htmlFor="visibility-private"
                    className="text-xs font-medium text-complementary-text cursor-pointer pt-1"
                  >
                    {t('modal.fields.visibility_private_label', {
                      defaultValue: 'Privada',
                    })}
                  </label>
                </div>
              </div>

              {/* Selector múltiple de teams (solo si es privada) */}
              {isPrivate && (
                <div className="sm:col-span-1">
                  <label className="text-sm font-medium text-text mb-1 block">
                    {t('modal.fields.teams_label', {
                      defaultValue: 'Teams',
                    })}
                  </label>
                  <MultiSelect
                    options={teamOptions}
                    value={selectedTeamIds}
                    onValueChange={setSelectedTeamIds}
                    placeholder={t('modal.fields.teams_placeholder', {
                      defaultValue: 'Seleccionar teams...',
                    })}
                    emptyText={t('modal.fields.teams_empty', {
                      defaultValue: 'No hay teams disponibles',
                    })}
                    maxDisplay={3}
                    className="w-full bg-card-bg"
                  />
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" size="lg" type="button" onClick={() => modalRef.current?.closeModal()}>
            {t('button.cancel')}
          </Button>
          <Button type="submit" variant="primary" size="lg" disabled={createMut.isPending}>
            {t('button.save')}
          </Button>
        </div>
      </form>
    </ModalCenter>
  );
});

CreateIntegrationModal.displayName = 'CreateIntegrationModal';
